import { ReactNode, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { SAFE_OVERLAY_MAX_HEIGHT } from '../../styles/layout.constants';
import { useResponsive } from '../../hooks/useResponsive';
import { useFormKeyboardShortcuts } from '../../hooks/useFormKeyboardShortcuts';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../../components/ui/sheet';

interface SecondaryAction {
  label: string;
  onClick: () => void;
  isLoading?: boolean;
  /**
   * Disable the secondary button on its OWN terms. The form-level `isDisabled`
   * deliberately does NOT reach it: that prop answers "is the form valid to
   * SUBMIT", and a secondary action is usually the way out for a user who has
   * not filled the form in — gating it on the submit predicate made it
   * unreachable in exactly the case it exists for.
   */
  isDisabled?: boolean;
}

interface DialogFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  submitLabel?: string;
  cancelLabel?: string;
  /**
   * The submit is running.
   *
   * Blocks a second submit and swaps the submit label for `loadingLabel`, but
   * deliberately does NOT close any way OUT of the dialog: not the Escape key,
   * not the overlay click, not the X, not Cancel. `onOpenChange` is Radix's
   * single funnel for the first two, so gating it on this flag left a slow
   * submit with no exit on any input device — and Radix holds
   * `body { pointer-events: none }` for as long as the content node is mounted,
   * which locked the whole page rather than just this footer (WI 4d87dc5b).
   */
  isLoading?: boolean;
  /**
   * What the submit button says while `isLoading`. Defaults to the translated
   * `common:app.processing` — the same neutral default `ConfirmDialog` takes,
   * and the only one that is never WRONG here: two thirds of this shell's
   * in-flight call sites submit "Create", not "Save". Pass the specific verb
   * where the action has one (`app.creating`, `app.saving`, "Deleting…") — a
   * spinner beside the unchanged idle label reads as a frozen dialog, not as
   * work in progress.
   */
  loadingLabel?: string;
  isDisabled?: boolean;
  destructive?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  hideActions?: boolean;
  /** Hide the Cancel button, leaving the primary action alone (e.g. a terminal
   *  success state where the only sensible action is a single Close). */
  hideCancel?: boolean;
  /** Force fullscreen on all screen sizes (useful for complex forms) */
  forceFullScreen?: boolean;
  /** Auto-focus the first input field when the dialog opens (default: true) */
  autoFocusFirst?: boolean;
  /** Optional secondary action button rendered between Cancel and the primary button */
  secondaryAction?: SecondaryAction;
}

/**
 * Every focusable form field, in DOM order. `[role="combobox"]` covers the Radix
 * `Select` trigger and the shared `EntityPickerBase` trigger — both render as a
 * `<button>`, so an `input`/`textarea`/`select` search misses them entirely.
 */
const FIELD_SELECTOR = [
  'input[type="text"]:not([disabled])',
  'input[type="email"]:not([disabled])',
  'input[type="tel"]:not([disabled])',
  'input[type="number"]:not([disabled])',
  'input[type="password"]:not([disabled])',
  'input:not([type]):not([disabled])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[role="combobox"]:not([disabled])',
].join(',');

/**
 * The house style for a field-level validation error: an `aria-invalid` control,
 * a destructive-bordered trigger, or the `<p className="text-sm text-destructive">`
 * message rendered under the field (see `docs/design-system/ui-form-errors.md`).
 * Only markers that appear AFTER a submit attempt count, so a body that already
 * renders something destructive-styled (an overdue badge, a warning chip) is not
 * mistaken for the field that blocked the submit.
 */
const ERROR_SELECTOR =
  '[aria-invalid="true"],.border-destructive,p.text-destructive';

/**
 * How long to keep watching for the error after a submit attempt. Client-side
 * rules paint within a frame, but the same submit can also come back as a
 * backend `VALIDATION_ERROR` mapped onto the field a network round-trip later.
 */
const ERROR_REVEAL_WINDOW_MS = 8000;

const maxWidthClasses = {
  xs: 'sm:max-w-xs',
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
};

export function DialogForm({
  open,
  onClose,
  onSubmit,
  title,
  description,
  children,
  submitLabel,
  cancelLabel,
  isLoading = false,
  loadingLabel,
  isDisabled = false,
  destructive = false,
  maxWidth = 'sm',
  hideActions = false,
  hideCancel = false,
  forceFullScreen = false,
  autoFocusFirst = true,
  secondaryAction,
}: DialogFormProps) {
  const { t } = useTranslation('common');
  const { isMobile } = useResponsive();
  // While something the dialog started is in flight, the way out says "Close",
  // not "Cancel": dismissing the dialog does not recall a request the server is
  // already serving, and a "Cancel" that cannot abort is a lie. An explicit
  // caller label always wins.
  const actionInFlight = isLoading || secondaryAction?.isLoading === true;
  const resolvedSubmitLabel = isLoading
    ? (loadingLabel ?? t('app.processing'))
    : (submitLabel ?? t('app.save'));
  const resolvedCancelLabel =
    cancelLabel ?? (actionInFlight ? t('app.close') : t('app.cancel'));
  const isFullScreen = forceFullScreen || isMobile;
  const contentRef = useRef<HTMLDivElement>(null);
  // When there is no description, opt out of Radix's aria-describedby requirement
  // instead of duplicating the title into an sr-only description (which reads as a
  // redundant verbatim subtitle in the accessibility tree). Passing the prop only
  // in the no-description case preserves Radix's own title↔description wiring when
  // a description IS provided. See Radix Dialog `DescriptionWarning`.
  const describedByOptOut = description
    ? {}
    : { 'aria-describedby': undefined };

  // Focus the first FIELD when the dialog opens.
  // Called from onOpenAutoFocus which fires after Radix mounts the content.
  //
  // The match is by DOM order, not by element type: a Radix `Select` / picker
  // trigger is a `<button role="combobox">`, so a type-ranked search ("inputs
  // first, then textareas") skipped every leading Select and landed on the LAST
  // text field in the form — which scrolled a mobile sheet clean past its own
  // required fields (INB-J5).
  const focusFirstInput = useCallback(() => {
    if (!autoFocusFirst || !contentRef.current) return;

    const candidates =
      contentRef.current.querySelectorAll<HTMLElement>(FIELD_SELECTOR);
    for (const candidate of candidates) {
      // offsetParent check ensures element is visible
      if (candidate.offsetParent !== null) {
        candidate.focus();
        return;
      }
    }
  }, [autoFocusFirst]);

  // Bring the first validation error into view after a failed submit. The
  // dialog body scrolls independently, so on a tall form (especially a
  // full-height mobile sheet) the field that blocked the submit can sit
  // off-screen and the primary button reads as simply dead.
  //
  // The error can land a frame later (a client-side rule) or a round-trip later
  // (a backend VALIDATION_ERROR mapped onto the field), so watch the body for
  // it instead of guessing a delay — and ignore markers that were already there
  // before the submit, which are decoration, not the blocking field.
  const errorWatchRef = useRef<{
    observer: MutationObserver;
    timer: number;
  } | null>(null);

  const stopErrorWatch = useCallback(() => {
    if (!errorWatchRef.current) return;
    errorWatchRef.current.observer.disconnect();
    window.clearTimeout(errorWatchRef.current.timer);
    errorWatchRef.current = null;
  }, []);

  useEffect(() => stopErrorWatch, [stopErrorWatch]);

  const watchForFirstError = useCallback(() => {
    const container = contentRef.current;
    if (!container) return;
    stopErrorWatch();

    const preexisting = new Set(
      container.querySelectorAll<HTMLElement>(ERROR_SELECTOR)
    );
    const observer = new MutationObserver(() => {
      const firstError = Array.from(
        container.querySelectorAll<HTMLElement>(ERROR_SELECTOR)
      ).find((node) => !preexisting.has(node));
      if (!firstError) return;

      stopErrorWatch();
      firstError.scrollIntoView({ block: 'center' });
      // Move the caret to the field itself so keyboard and screen-reader users
      // land on what blocked them, not just sighted ones. The message <p> sits
      // next to its control inside the field wrapper.
      const field = firstError.matches(FIELD_SELECTOR)
        ? firstError
        : firstError.parentElement?.querySelector<HTMLElement>(FIELD_SELECTOR);
      field?.focus({ preventScroll: true });
    });

    observer.observe(container, {
      subtree: true,
      childList: true,
      attributes: true,
    });
    errorWatchRef.current = {
      observer,
      timer: window.setTimeout(stopErrorWatch, ERROR_REVEAL_WINDOW_MS),
    };
  }, [stopErrorWatch]);

  const handleSubmit = useCallback(() => {
    watchForFirstError();
    onSubmit?.();
  }, [onSubmit, watchForFirstError]);

  // Ctrl/Cmd+Enter submits through the same wrapper, so a keyboard submit also
  // reveals the blocking field.
  const { handleKeyDown } = useFormKeyboardShortcuts({
    onSubmit: onSubmit ? handleSubmit : undefined,
    onCancel: onClose,
    isLoading,
  });

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      // No `isLoading` gate. This one handler is Escape AND the overlay click,
      // so gating it took away both at once — see the `isLoading` prop doc.
      if (!newOpen) {
        onClose();
      }
    },
    [onClose]
  );

  // Mobile: Full-screen Sheet
  if (isFullScreen) {
    return (
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          side="bottom"
          // `p-0` opts out of SheetContent's own safe-area padding, so this
          // dialog owes the insets to its own regions: the header pads past the
          // status bar, and the scrolling body pads past the navigation bar (see
          // both below). `100dvh` is deliberate — the sheet's SURFACE runs to
          // the physical screen edge and only its content is pushed clear.
          className="flex h-[100dvh] flex-col p-0"
          hideCloseButton
          {...describedByOptOut}
          onKeyDown={handleKeyDown}
          onOpenAutoFocus={(e) => {
            // Prevent Radix's default focus behavior - we handle it ourselves
            if (autoFocusFirst) {
              e.preventDefault();
              // Use double requestAnimationFrame to ensure React has rendered form fields
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  focusFirstInput();
                });
              });
            }
          }}
        >
          {/* Header — `pt-safe-top` clears the status bar the sheet runs under. */}
          <div className="shrink-0 border-b bg-background pt-safe-top">
            <div className="flex items-center justify-between px-4 py-3">
              {/* Never disabled. The X is the only dismissal control this
                  shell always renders — it survives `hideActions` and
                  `hideCancel` — so on a touch device, which has no Escape key,
                  it is the guaranteed way out of an in-flight submit. */}
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="h-11 w-11"
                data-testid="dialog-close"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">{t('app.close')}</span>
              </Button>
              <SheetHeader className="min-w-0 flex-1 px-2">
                <SheetTitle className="truncate text-center text-base">
                  {title}
                </SheetTitle>
                {description && (
                  <SheetDescription className="text-center text-xs">
                    {description}
                  </SheetDescription>
                )}
              </SheetHeader>
              {!hideActions && onSubmit && (
                <Button
                  size="sm"
                  variant={destructive ? 'destructive' : 'default'}
                  className="h-11 min-w-11"
                  onClick={handleSubmit}
                  disabled={
                    isLoading || isDisabled || secondaryAction?.isLoading
                  }
                  data-testid="dialog-submit"
                >
                  {isLoading && (
                    <Loader2 className="me-2 h-4 w-4 animate-spin" />
                  )}
                  {resolvedSubmitLabel}
                </Button>
              )}
            </div>
            {!hideActions && secondaryAction && (
              <div className="px-4 pb-3">
                <Button
                  variant="outline"
                  className="h-11 w-full"
                  onClick={secondaryAction.onClick}
                  disabled={
                    isLoading ||
                    secondaryAction.isLoading ||
                    secondaryAction.isDisabled
                  }
                  data-testid="dialog-secondary"
                >
                  {secondaryAction.isLoading && (
                    <Loader2 className="me-2 h-4 w-4 animate-spin" />
                  )}
                  {secondaryAction.label}
                </Button>
              </div>
            )}
          </div>
          {/* Content.
              The bottom padding carries the navigation-bar inset on top of its
              own 1rem. `hideActions` + a caller-rendered action row is a
              sanctioned pattern (ui-dialogs.md § Button Layout), and that row is
              the LAST child of this scroll area — so without the inset the
              buttons come to rest underneath the Android navigation bar and
              cannot be tapped at all. */}
          <div
            ref={contentRef}
            className="flex-1 overflow-y-auto p-4 px-[calc(1rem+4px)] pb-[calc(1rem+var(--safe-bottom,0px))] -mx-1"
          >
            {children}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop: Standard Dialog
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          // The height cap subtracts both system-bar insets: on a tablet-sized
          // Capacitor shell this arm (not the mobile Sheet) is what renders, and
          // a plain 90vh box centred on the safe region still overhangs it.
          'sm:max-w-[425px] flex flex-col',
          maxWidthClasses[maxWidth]
        )}
        style={{ maxHeight: SAFE_OVERLAY_MAX_HEIGHT }}
        hideCloseButton
        {...describedByOptOut}
        onKeyDown={handleKeyDown}
        onOpenAutoFocus={(e) => {
          // Prevent Radix's default focus behavior - we handle it ourselves
          if (autoFocusFirst) {
            e.preventDefault();
            // Use double requestAnimationFrame to ensure React has rendered form fields
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                focusFirstInput();
              });
            });
          }
        }}
      >
        <DialogHeader className="flex flex-row items-center justify-between space-y-0 shrink-0">
          <div className="flex-1 space-y-1">
            <DialogTitle>{title}</DialogTitle>
            {description && (
              <DialogDescription>{description}</DialogDescription>
            )}
          </div>
          {/* Never disabled — see the mobile header's X. */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8"
            data-testid="dialog-close"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">{t('app.close')}</span>
          </Button>
        </DialogHeader>
        <div
          ref={contentRef}
          className="py-4 px-1 -mx-1 overflow-y-auto min-h-0"
        >
          {children}
        </div>
        {!hideActions && (
          <DialogFooter className="shrink-0">
            {/* Cancel is never disabled either: a way out that dies the moment
                the user needs it is not a way out. It reads "Close" while
                something is in flight — see `resolvedCancelLabel`. */}
            {!hideCancel && (
              <Button
                variant="outline"
                onClick={onClose}
                data-testid="dialog-cancel"
              >
                {resolvedCancelLabel}
              </Button>
            )}
            {secondaryAction && (
              <Button
                variant="outline"
                onClick={secondaryAction.onClick}
                disabled={
                  isLoading ||
                  secondaryAction.isLoading ||
                  secondaryAction.isDisabled
                }
              >
                {secondaryAction.isLoading && (
                  <Loader2 className="me-2 h-4 w-4 animate-spin" />
                )}
                {secondaryAction.label}
              </Button>
            )}
            {onSubmit && (
              <Button
                variant={destructive ? 'destructive' : 'default'}
                onClick={handleSubmit}
                disabled={isLoading || isDisabled || secondaryAction?.isLoading}
                data-testid="dialog-submit"
              >
                {isLoading && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                {resolvedSubmitLabel}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
